// Strip field: fitting type dropdown or selected pill with Change link.
// Not mounted in BookingStripPanel by default (location-only strip); kept
// for reuse if the three-field strip layout returns.

import { useState } from 'react';
import styles from '../styles/widget.module.css';
import { visibleFittingTypes, pricingLabelFor, fittingDisplayFor } from '../data/fittingTypes';
import { formatPricingLines } from '../utils/pricing';
import { promoPricingLines } from '../data/promoPricing';
import { promoPriceShowsFlatFee } from '../config/countries';
import type { BookingAction, BookingState } from '../state/machine';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOpenClose } from '../hooks/useOpenClose';
import ChevronDown from '../assets/icons/ChevronDown';
import SimplePopup from './SimplePopup';
import { resolveWireName } from '../data/fittingNames';

interface FittingTypeFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled?: boolean;
  hideSelectedPill?: boolean;
  /**
   * Open the picker immediately on mount. For hosts (the seed card) whose own
   * Change control just cleared the selection — landing on a closed "Select
   * Fitting Type" trigger there reads as the click having done nothing.
   */
  autoOpen?: boolean;
  /** Called after a fitting is picked, so an embedding host can put its own row back. */
  onPick?: () => void;
}

export function FittingTypeField({
  state,
  dispatch,
  disabled = false,
  hideSelectedPill = false,
  autoOpen = false,
  onPick,
}: FittingTypeFieldProps) {
  const [open, setOpen] = useState(autoOpen);
  const { isOpen: isOpenFittingTypeInfo, close: closeFittingTypeInfo, open: openFittingTypeInfo } = useOpenClose()
  const wrapperRef = useClickOutside<HTMLDivElement>(() => {
    setOpen(false);
  }, { enabled: open });

  if (state.selectedFittingType) {
    if (hideSelectedPill) return null;
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Fitting Type</span>
        <div className={styles.stripPillValue}>
          <span>{state.selectedFittingType.displayName ?? state.selectedFittingType.name}</span>
          <button
            type="button"
            className={styles.stripPillChange}
            onClick={() => dispatch({ type: 'SELECT_FITTING_TYPE', payload: null })}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.field} ${disabled ? styles.panelSectionDisabled : ''}`} ref={wrapperRef}>
      <label className={styles.label}>
        Fitting Type
        <button type='button' onClick={openFittingTypeInfo} className={styles.learnMoreButton}>
          Learn More
        </button>
      </label>
      <SimplePopup
        children={
          <div>
            <div>
              <p>Club Champion offers many fitting<br />types at various durations.<br /><br />Scroll to explore and see our<br />most popular options.</p>
            </div>
          </div>
        }
        isOpen={isOpenFittingTypeInfo}
        onClosePopup={closeFittingTypeInfo} className={styles.fittingTypePopupMobile} />

      {/* type="button", both: these render inside the seed card's FORM,
          where an untyped button defaults to submit — clicking "Select
          Fitting Type" was submitting Step 1 and launching the flow. */}
      <button
        type="button"
        className={styles.input}
        style={{ textAlign: 'left', cursor: 'pointer', background: '#fff' }}
        onClick={() => setOpen(!open)}
      >
        <span>Select Fitting Type</span>
        <ChevronDown />
      </button>
      {open && (() => {
        const visible = visibleFittingTypes(state.country, state.selectedLocation);
        return (
          <div className={styles.locationDropdown} style={{ maxWidth: 360 }}>
            <div className={styles.locationDropdownHeader}>
              {visible.length} Fitting {visible.length === 1 ? 'Type' : 'Types'} to Choose From
            </div>
            {visible.map((ft) => {
              const disp = fittingDisplayFor(ft, state.country);
              return (
                <div
                  key={ft.id}
                  className={styles.locationRow}
                  onClick={() => {
                    dispatch({ type: 'SELECT_FITTING_TYPE', payload: ft });
                    setOpen(false);
                    onPick?.();
                  }}
                >
                  <div className={styles.fittingTypeHeader}>
                    <span className={styles.fittingTypeName}>{disp.displayName.toUpperCase()}</span>
                    {ft.badge && <span className={styles.fittingTypeBadge}>{ft.badge}</span>}
                  </div>
                  <div className={styles.fittingTypeDescription}>{ft.description}</div>
                  <div className={styles.fittingTypeMeta}>
                    <span>{disp.durationLabel}</span>
                    {(() => {
                      // Salesforce-resolved price first; the authored label is
                      // only a fallback for before resolution lands.
                      const wireName = resolveWireName(ft.name, state.fittingNameMap);
                      const { line1, line2 } =
                        promoPricingLines(
                          state.promoPricing?.[wireName],
                          promoPriceShowsFlatFee(state.country),
                          state.country,
                        ) ??
                        formatPricingLines(
                          pricingLabelFor(ft, state.country),
                          promoPriceShowsFlatFee(state.country),
                        );
                      return (
                        <>
                          <span>
                            {line1.bold && <strong>{line1.bold}</strong>}
                            {line1.rest}
                          </span>
                          <span>
                            {line2.bold && <strong>{line2.bold}</strong>}
                            {line2.rest}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}
